import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  BookOpen, Sparkles, Loader2, Trash2, RefreshCw, Flame, Lock, Smile,
} from "lucide-react";

const MOODS = [
  { id: "great", label: "Great", emoji: "😄", color: "bg-[#FFEDD5] text-[#C2410C]" },
  { id: "good", label: "Good", emoji: "🙂", color: "bg-[#FEF3C7] text-[#B45309]" },
  { id: "okay", label: "Okay", emoji: "😐", color: "bg-[#E5E7EB] text-[#374151]" },
  { id: "low", label: "Low", emoji: "😕", color: "bg-[#DBEAFE] text-[#1E40AF]" },
  { id: "rough", label: "Rough", emoji: "😔", color: "bg-[#FEE2E2] text-[#B91C1C]" },
];

const moodMeta = (id) => MOODS.find((m) => m.id === id);

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function Journal() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [streak, setStreak] = useState(0);
  const [recentMoods, setRecentMoods] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [mood, setMood] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reflectingId, setReflectingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadEntries = async () => {
    try {
      const { data } = await api.get("/journal");
      setEntries(data.entries || []);
      setStreak(data.streak || 0);
      setRecentMoods(data.recent_moods || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const loadPrompt = async () => {
    try { const { data } = await api.get("/journal/prompt"); setPrompt(data.prompt); }
    catch { /* ignore */ }
  };

  useEffect(() => { loadEntries(); loadPrompt(); }, []);

  const save = async () => {
    const text = content.trim();
    if (!text) { toast.error("Write something first."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/journal", { content: text, mood, title: title.trim() || null });
      setEntries((prev) => [data.entry, ...prev]);
      setContent(""); setTitle(""); setMood(null);
      toast.success("Entry saved.");
      if (data.unlocked?.length) {
        data.unlocked.forEach((u) => toast.success(`Badge unlocked: ${u.title}`));
      }
      loadEntries(); // refresh streak
    } catch (e) {
      !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const reflect = async (id) => {
    setReflectingId(id);
    try {
      const { data } = await api.post(`/journal/${id}/reflect`);
      setEntries((prev) => prev.map((e) => e.id === id ? { ...e, ai_reflection: data.ai_reflection } : e));
    } catch (e) {
      !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail));
    } finally { setReflectingId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/journal/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const canReflect = user?.plan && user.plan !== "free";

  return (
    <div className="space-y-6" data-testid="journal-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sunrise text-[#2D3748]">
            <BookOpen className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-nunito text-2xl font-bold text-[#2D3748]">Journal</h1>
            <p className="font-quicksand text-sm text-[#718096]">Reflect daily. Grow forward.</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 rounded-full bg-[#FFEDD5] px-4 py-2 text-[#C2410C]" data-testid="journal-streak">
            <Flame className="h-4 w-4" strokeWidth={2.5} />
            <span className="font-nunito text-sm font-bold">{streak}-day streak</span>
          </div>
        </div>
      </div>

      {/* Mood strip */}
      {recentMoods.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="journal-recent-moods">
          <Smile className="h-4 w-4 text-[#718096]" />
          <span className="font-quicksand text-xs text-[#718096]">Recent:</span>
          {recentMoods.slice(0, 10).map((m, i) => {
            const meta = moodMeta(m);
            return meta ? (
              <span key={i} className={`rounded-full px-2 py-0.5 text-xs ${meta.color}`} title={meta.label}>{meta.emoji}</span>
            ) : null;
          })}
        </div>
      )}

      {/* Composer */}
      <div className="rounded-3xl border border-[#FFE8E3] bg-white p-6 shadow-sm">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <p className="font-quicksand text-xs uppercase tracking-wider text-[#C2410C]">Today&rsquo;s prompt</p>
            <p className="mt-1 font-nunito text-base font-bold text-[#2D3748]" data-testid="journal-prompt">
              {prompt || "Loading…"}
            </p>
          </div>
          <button onClick={loadPrompt} data-testid="journal-refresh-prompt-btn"
            className="rounded-full p-2 text-[#718096] hover:bg-[#FFF9F5] hover:text-[#FF7E67]" title="New prompt">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)"
          data-testid="journal-title-input"
          className="mb-3 w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-2.5 font-quicksand text-sm text-[#2D3748] outline-none focus:border-[#FF7E67]" />

        <textarea value={content} onChange={(e) => setContent(e.target.value)}
          placeholder="What&rsquo;s on your mind today?" rows={6}
          data-testid="journal-content-input"
          className="w-full resize-none rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3 font-quicksand text-sm text-[#2D3748] outline-none focus:border-[#FF7E67]" />

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2" data-testid="journal-mood-picker">
            {MOODS.map((m) => (
              <button key={m.id} type="button" onClick={() => setMood(mood === m.id ? null : m.id)}
                data-testid={`mood-${m.id}`}
                className={`rounded-full border px-3 py-1.5 font-nunito text-xs font-bold transition-all ${
                  mood === m.id
                    ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C] scale-105"
                    : "border-[#FFE8E3] bg-white text-[#718096] hover:border-[#FF7E67]"
                }`}>
                <span className="mr-1">{m.emoji}</span>{m.label}
              </button>
            ))}
          </div>
          <button onClick={save} disabled={saving || !content.trim()}
            data-testid="journal-save-btn"
            className="flex items-center gap-2 rounded-full bg-sunrise px-5 py-2.5 font-nunito text-sm font-bold text-white shadow-glow-coral transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
            Save entry
          </button>
        </div>
      </div>

      {/* Entries */}
      <div>
        <h2 className="mb-3 font-nunito text-lg font-bold text-[#2D3748]">Your entries</h2>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#FF7E67]" /></div>
        ) : entries.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[#FFE8E3] bg-white p-10 text-center">
            <BookOpen className="mx-auto mb-3 h-8 w-8 text-[#FF7E67]" />
            <p className="font-quicksand text-sm text-[#718096]">No entries yet. Start with today&rsquo;s prompt above.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="journal-entries-list">
            <AnimatePresence>
              {entries.map((e) => {
                const meta = moodMeta(e.mood);
                return (
                  <motion.div key={e.id} layout
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                    className="rounded-3xl border border-[#FFE8E3] bg-white p-5 shadow-sm"
                    data-testid={`journal-entry-${e.id}`}>
                    <div className="mb-2 flex items-start justify-between gap-3">
                      <div>
                        {e.title && <p className="font-nunito text-base font-bold text-[#2D3748]">{e.title}</p>}
                        <div className="flex items-center gap-2">
                          <p className="font-quicksand text-xs text-[#718096]">{fmtDate(e.created_at)}</p>
                          {meta && (
                            <span className={`rounded-full px-2 py-0.5 text-xs ${meta.color}`}>{meta.emoji} {meta.label}</span>
                          )}
                        </div>
                      </div>
                      <button onClick={() => remove(e.id)} data-testid={`journal-delete-${e.id}`}
                        className="rounded-full p-2 text-[#718096] hover:bg-[#FEE2E2] hover:text-[#B91C1C]" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="whitespace-pre-wrap font-quicksand text-sm text-[#2D3748]">{e.content}</p>

                    {e.ai_reflection ? (
                      <div className="mt-4 rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] p-4">
                        <div className="mb-1 flex items-center gap-2 text-[#C2410C]">
                          <Sparkles className="h-4 w-4" strokeWidth={2.5} />
                          <span className="font-nunito text-xs font-bold uppercase tracking-wider">Reflection</span>
                        </div>
                        <p className="whitespace-pre-wrap font-quicksand text-sm text-[#2D3748]">{e.ai_reflection}</p>
                      </div>
                    ) : (
                      <button onClick={() => reflect(e.id)} disabled={reflectingId === e.id}
                        data-testid={`journal-reflect-${e.id}`}
                        className={`mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 font-nunito text-xs font-bold transition-colors ${
                          canReflect
                            ? "bg-[#FFE8E3] text-[#C2410C] hover:bg-[#FFD4C7]"
                            : "bg-[#F3F4F6] text-[#718096] hover:bg-[#FFE8E3] hover:text-[#C2410C]"
                        }`}>
                        {reflectingId === e.id
                          ? <><Loader2 className="h-3 w-3 animate-spin" /> Reflecting…</>
                          : canReflect
                            ? <><Sparkles className="h-3 w-3" strokeWidth={2.5} /> Reflect with AI</>
                            : <><Lock className="h-3 w-3" strokeWidth={2.5} /> Reflect with AI · Plus</>}
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
